import { useEffect, useState } from "react";
import { fetchCombinedAutocomplete } from "../api/v0/search/fetchCombinedAutocomplete";

const cache = new Map<string, string>();

/**
 * Resolve a canonical destinationId from a destination label by querying the
 * combined autocomplete API.
 *
 * Used by pages that know the human-readable destination label (e.g. the shop
 * page's `locationLabel`) but need the canonical id to scope category /
 * attraction / product searches. Resolving up-front and passing the id
 * explicitly is preferable to relying on the search saga's locationLabel
 * fallback because the page owns the destination context, not the search
 * component.
 *
 * Results are cached at module scope so repeat visits with the same label
 * return synchronously on first render.
 */
export function useResolveDestinationId(
  label: string | undefined
): string | undefined {
  const [destinationId, setDestinationId] = useState<string | undefined>(() =>
    label ? cache.get(label) : undefined
  );

  useEffect(() => {
    if (!label) {
      setDestinationId(undefined);
      return undefined;
    }

    const cached = cache.get(label);
    if (cached) {
      setDestinationId(cached);
      return undefined;
    }

    // Clear the previous resolution before kicking off a new fetch so
    // consumers don't keep reading the prior label's id while the new
    // one is in flight.
    setDestinationId(undefined);

    let cancelled = false;
    (async () => {
      try {
        const response = await fetchCombinedAutocomplete({ query: label });
        if (cancelled) return;

        // groupType may be a plain string or a Scala-style envelope
        // like { CombinedSearchResultType: "Destination" }.
        const isDestinationGroup = (g: { groupType?: unknown }): boolean => {
          const raw = g.groupType as
            | string
            | { CombinedSearchResultType?: string }
            | undefined;
          const type =
            typeof raw === "object" && raw !== null
              ? raw.CombinedSearchResultType
              : raw;
          return type === "Destination";
        };

        const destGroup = (response.groups ?? []).find(isDestinationGroup);
        const first = destGroup?.suggestions?.[0];
        if (!first) return;

        // BE leaves `destinationId` empty on Destination suggestions —
        // `id` is the destination's canonical id. Match the pattern in
        // fetchTripDestinationsSaga.ts.
        const resolvedId = first.destinationId || first.id;
        if (resolvedId) {
          cache.set(label, resolvedId);
          setDestinationId(resolvedId);
        }
      } catch {
        // Resolution failed — leave undefined; the search will be unscoped
        // until the user picks a destination.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [label]);

  return destinationId;
}
